/**
 * EndLessonReviewNudge — calm, post-End-Lesson behavioural prompt.
 *
 * Lives in the Work Mode FEEDBACK stage, immediately above the
 * WorkModeAIReviewSection. Goal: make AI Review a natural end-of-lesson
 * habit without forcing it.
 *
 * Behaviour rules (owner-locked):
 *   • Never blocks flow.
 *   • Never forces — Skip is always one tap away.
 *   • Disappears once the instructor picks an action OR after they've
 *     moved past this lesson (per-lesson localStorage dismissal).
 *
 * Tracking:
 *   • POST /api/lesson-review/nudge with event ∈ {shown, upload_now,
 *     skip} so we can later analyse whether uploads happen within 5
 *     minutes of lesson end.
 *
 * Visual: small calm slate card with a single line of copy and two
 * buttons. Mobile-first, thumb-sized.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, Upload, X } from "lucide-react";
import LessonVideoUploadModal from "./LessonVideoUploadModal";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

function dismissKey(lessonId) {
  return `wm_review_nudge_dismissed_${lessonId || "ad-hoc"}`;
}

async function postEvent(event, payload) {
  // Best-effort — never raise into the UI. Behavioural tracking
  // should never block the user. Quarantine guard so we don't
  // even fire the beacon while media is quarantined.
  if (!MEDIA_FEATURE_ENABLED) return;
  try {
    await axios.post(
      `${API}/api/lesson-review/nudge`,
      { event, ...payload },
      { withCredentials: true }
    );
  } catch {
    // swallow — tracking is observational only
  }
}

export default function EndLessonReviewNudge({
  pupilId,
  pupilName,
  lessonId,
  lessonEndedAt,
  onUploaded,
}) {
  // Per-lesson dismissal — stays gone for this lesson but reappears
  // for the next one.
  const dismissed = (() => {
    try {
      return Boolean(localStorage.getItem(dismissKey(lessonId)));
    } catch {
      return false;
    }
  })();

  const [hidden, setHidden] = useState(dismissed);
  const [open, setOpen] = useState(false);
  const shownLoggedRef = useRef(false);

  // Log "shown" exactly once per mount (per lesson) — this is our
  // anchor for the "did upload happen within 5 minutes" analytics.
  useEffect(() => {
    if (hidden || shownLoggedRef.current) return;
    shownLoggedRef.current = true;
    postEvent("shown", {
      pupil_id: pupilId,
      lesson_id: lessonId,
      lesson_ended_at: lessonEndedAt,
    });
  }, [hidden, pupilId, lessonId, lessonEndedAt]);

  if (hidden) return null;
  // Media quarantine — silent. No upload-now nudge surfaces while
  // /api/lesson-review/* is quarantined.
  if (!MEDIA_FEATURE_ENABLED) return null;

  const onUploadNow = () => {
    postEvent("upload_now", {
      pupil_id: pupilId,
      lesson_id: lessonId,
      lesson_ended_at: lessonEndedAt,
    });
    setOpen(true);
  };

  const onSkip = () => {
    postEvent("skip", {
      pupil_id: pupilId,
      lesson_id: lessonId,
      lesson_ended_at: lessonEndedAt,
    });
    try {
      localStorage.setItem(dismissKey(lessonId), "1");
    } catch {
      // localStorage may be unavailable in some embedded contexts
    }
    setHidden(true);
  };

  const onUploadedInner = (data) => {
    // Mark a follow-up "uploaded" event so "within 5 min" can be
    // computed precisely (some uploads start fast, then take >30s to
    // finish — we want the click + the completion).
    postEvent("uploaded", {
      pupil_id: pupilId,
      lesson_id: lessonId,
      lesson_ended_at: lessonEndedAt,
      review_id: data?.review?.id,
    });
    try {
      localStorage.setItem(dismissKey(lessonId), "1");
    } catch {
      // ignore
    }
    setHidden(true);
    setOpen(false);
    onUploaded?.(data);
  };

  return (
    <>
      <div
        data-testid="wm-end-lesson-nudge"
        className="rounded-xl border border-indigo-700/40 bg-gradient-to-br from-indigo-900/30 to-slate-900/30 p-3 flex items-center gap-3"
      >
        <div className="flex-none w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-indigo-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            data-testid="wm-end-lesson-nudge-prompt"
            className="text-[13px] font-bold text-white leading-snug"
          >
            Quick review now = better next lesson
          </p>
          <p className="text-[11px] text-indigo-200/80 leading-snug">
            Free for instructors — processing starts straight away.
          </p>
        </div>
        <div className="flex-none flex items-center gap-1.5">
          <button
            type="button"
            data-testid="wm-end-lesson-nudge-upload-now"
            onClick={onUploadNow}
            className="h-9 px-3 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-[12px] font-extrabold flex items-center gap-1.5 transition active:scale-[0.98]"
          >
            <Upload className="w-3.5 h-3.5" /> Upload now
          </button>
          <button
            type="button"
            data-testid="wm-end-lesson-nudge-skip"
            onClick={onSkip}
            aria-label="Skip for now"
            className="h-9 w-9 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 flex items-center justify-center transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {open && (
        <LessonVideoUploadModal
          pupilId={pupilId}
          pupilName={pupilName}
          lessonId={lessonId}
          onClose={() => setOpen(false)}
          onUploaded={onUploadedInner}
        />
      )}
    </>
  );
}
